import React, { useEffect, useMemo, useState } from "react";
import AdminHeader from "../../../../components/AdminHeader";
import AdminPageHeader from "../../../../components/admin/AdminPageHeader";
import {
  adminCreateMedia,
  adminDeleteMedia,
  adminListMedia,
  adminUpdateMedia,
} from "../../../../services/admin";
import { getCurrentSession } from "../../../../services/auth";

const API_URL = (
  process.env.REACT_APP_API_URL || "https://zedro-backend.onrender.com"
).replace(/\/$/, "");

const ui = {
  page: {
    maxWidth: 1180,
    margin: "2rem auto",
    fontFamily: "Questrial, sans-serif",
  },

  card: {
    border: "1px solid rgba(44,59,81,.12)",
    borderRadius: 18,
    background: "#fff",
    boxShadow: "0 14px 34px rgba(15,47,70,.06)",
  },

  cardPad: { padding: 18 },

  layout: {
    display: "grid",
    gridTemplateColumns: "360px 1fr",
    gap: 18,
    alignItems: "start",
  },

  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 900,
    color: "rgba(44,59,81,.72)",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: ".06em",
  },

  input: {
    width: "100%",
    padding: "11px 12px",
    borderRadius: 12,
    border: "1px solid rgba(44,59,81,.14)",
    outline: "none",
    fontSize: 14,
    background: "#fbfcfe",
    boxSizing: "border-box",
  },

  textarea: {
    width: "100%",
    padding: "11px 12px",
    borderRadius: 12,
    border: "1px solid rgba(44,59,81,.14)",
    outline: "none",
    fontSize: 14,
    background: "#fbfcfe",
    resize: "vertical",
    minHeight: 100,
    boxSizing: "border-box",
  },

  btnRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 12,
    flexWrap: "wrap",
  },

  btn: {
    padding: "10px 14px",
    borderRadius: 999,
    border: "1px solid rgba(44,59,81,.16)",
    background: "#fff",
    color: "#0f2f46",
    fontWeight: 900,
    cursor: "pointer",
  },

  btnPrimary: {
    padding: "10px 16px",
    borderRadius: 999,
    border: "1px solid rgba(204,174,73,.35)",
    background: "linear-gradient(135deg, #0f2f46 0%, #173f5b 100%)",
    color: "#f4d27a",
    fontWeight: 900,
    cursor: "pointer",
  },

  miniBtn: {
    padding: "8px 10px",
    borderRadius: 999,
    border: "1px solid rgba(44,59,81,.16)",
    background: "#fff",
    color: "#0f2f46",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 12,
  },

  danger: {
    borderColor: "#f2c7c7",
    color: "#7b1f1f",
    background: "#fff5f5",
  },

  error: {
    padding: "10px 12px",
    borderRadius: 12,
    background: "#ffecec",
    color: "#7b1f1f",
    border: "1px solid #f2c7c7",
    marginBottom: 12,
  },
};

function getUseLabel(title = "") {
  const clean = String(title || "").toLowerCase();

  if (clean.startsWith("s")) return "Sistemas";
  if (clean.startsWith("zn-")) return "ZEDRO News";
  if (clean.includes("store") || clean.includes("membres")) return "Store";
  return "Otros";
}

function isImage(item) {
  return item?.kind === "image" && item?.url;
}

export default function ProduccionMediaHome() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [isVertical, setIsVertical] = useState(false);
  const [showForm, setShowForm] = useState(true);

  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [useFilter, setUseFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 12;

  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    kind: "image",
    provider: "",
    url: "",
    description: "",
    status: "draft",
  });

  async function load() {
    try {
      setLoading(true);
      setError("");

      const data = await adminListMedia();
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      console.error("[ADMIN] cannot_list_media", e);
      setError("No se pudo cargar Media.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, kindFilter, useFilter]);

  function reset() {
    setEditingId(null);
    setForm({
      title: "",
      kind: "image",
      provider: "",
      url: "",
      description: "",
      status: "draft",
    });
  }

  async function onSubmit(e) {
    e.preventDefault();

    setBusy(true);
    setError("");

    try {
      if (!form.title.trim()) return setError("El título es obligatorio.");
      if (!form.url.trim()) return setError("La URL es obligatoria.");

      const payload = {
        title: form.title.trim(),
        kind: form.kind,
        provider: form.provider.trim(),
        url: form.url.trim(),
        description: form.description.trim(),
        status: form.status,
      };

      if (editingId) {
        await adminUpdateMedia(editingId, payload);
      } else {
        await adminCreateMedia(payload);
      }

      reset();
      await load();
    } catch (e) {
      console.error("[ADMIN] cannot_save_media", e);
      setError("No se pudo guardar.");
    } finally {
      setBusy(false);
    }
  }

  function startEdit(it) {
    setEditingId(it.id);
    setShowForm(true);

    setForm({
      title: it.title || "",
      kind: it.kind || "image",
      provider: it.provider || "",
      url: it.url || "",
      description: it.description || "",
      status: it.status || "draft",
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function remove(id) {
    if (!window.confirm("¿Eliminar este item de Media?")) return;

    setBusy(true);
    setError("");

    try {
      await adminDeleteMedia(id);
      await load();
    } catch (e) {
      console.error("[ADMIN] cannot_delete_media", e);
      setError("No se pudo eliminar.");
    } finally {
      setBusy(false);
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const session = getCurrentSession();
    const token = session?.token;

    const authHeaders = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    const detectedKind = file.type.startsWith("video/")
      ? "video"
      : file.type.startsWith("audio/")
      ? "audio"
      : file.type.startsWith("image/")
      ? "image"
      : "file";

    const isVideo = detectedKind === "video";
    const ONE_HUNDRED_MB = 100 * 1024 * 1024;

    try {
      setUploading(true);
      setError("");

      let publicUrl = "";

      /*
        Los videos se cargan directamente desde el navegador
        hacia Cloudflare R2 mediante una URL temporal.
      */
      if (isVideo) {
        const presignResponse = await fetch(
          `${API_URL}/api/admin/media/presign`,
          {
            method: "POST",
            headers: {
              ...authHeaders,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              fileName: file.name,
              contentType: file.type,
              fileSize: file.size,
            }),
          }
        );

        const presignData = await presignResponse
          .json()
          .catch(() => ({}));

        if (
          !presignResponse.ok ||
          !presignData?.uploadUrl ||
          !presignData?.url
        ) {
          throw new Error(
            presignData?.error || "cannot_create_upload_url"
          );
        }

        const r2Response = await fetch(presignData.uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
          },
          body: file,
        });

        if (!r2Response.ok) {
          throw new Error(`r2_upload_failed_${r2Response.status}`);
        }

        publicUrl = presignData.url;
      } else {
        /*
          Imágenes, audios, PDF y archivos pequeños continúan
          utilizando el cargador normal del backend.
        */
        if (file.size > ONE_HUNDRED_MB) {
          throw new Error("non_video_file_too_large");
        }

        const formData = new FormData();
        formData.append("file", file);

        const uploadResponse = await fetch(
          `${API_URL}/api/admin/media/upload`,
          {
            method: "POST",
            headers: authHeaders,
            body: formData,
          }
        );

        const uploadData = await uploadResponse
          .json()
          .catch(() => ({}));

        if (!uploadResponse.ok || !uploadData?.url) {
          throw new Error(uploadData?.error || "upload_failed");
        }

        publicUrl = uploadData.url;
      }

      setForm((prev) => ({
        ...prev,
        kind: detectedKind,
        provider: "R2",
        url: publicUrl,
      }));
    } catch (err) {
      console.error("[ADMIN] media upload error", err);

      if (err.message === "unsupported_file_type") {
        setError("Este formato de video no está permitido.");
      } else if (
        err.message === "file_too_large" ||
        err.message === "non_video_file_too_large"
      ) {
        setError("El archivo supera el tamaño permitido.");
      } else if (err.message.startsWith("r2_upload_failed")) {
        setError(
          "Cloudflare R2 no pudo recibir el archivo. Revisaremos la configuración CORS."
        );
      } else {
        setError("Error subiendo archivo.");
      }
    } finally {
      setUploading(false);

      // Permite seleccionar nuevamente el mismo archivo si fuera necesario.
      e.target.value = "";
    }
  }

  async function copyUrl(url) {
    try {
      await navigator.clipboard.writeText(url);
      alert("URL copiada.");
    } catch (err) {
      console.error("[ADMIN] cannot_copy_url", err);
      alert("No se pudo copiar la URL.");
    }
  }

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const title = String(item.title || "").toLowerCase();
      const description = String(item.description || "").toLowerCase();
      const url = String(item.url || "").toLowerCase();
      const q = query.trim().toLowerCase();

      const matchesQuery =
        !q || title.includes(q) || description.includes(q) || url.includes(q);

      const matchesKind = kindFilter === "all" || item.kind === kindFilter;
      const matchesUse = useFilter === "all" || getUseLabel(item.title) === useFilter;

      return matchesQuery && matchesKind && matchesUse;
    });
  }, [items, query, kindFilter, useFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const visibleItems = filteredItems.slice(pageStart, pageEnd);

  const rangeStart = filteredItems.length === 0 ? 0 : pageStart + 1;
  const rangeEnd = Math.min(pageEnd, filteredItems.length);

  const goPreviousPage = () => {
    setCurrentPage((page) => Math.max(1, page - 1));
  };

  const goNextPage = () => {
    setCurrentPage((page) => Math.min(totalPages, page + 1));
  };

  return (
    <>
      <AdminHeader />

      <main className="admin-page" style={ui.page}>
        <AdminPageHeader
          title="Media"
          subtitle="Organiza imágenes, audios, videos y archivos reutilizables para ZEDRO."
          backTo="/admin/produccion"
        />

        <div style={ui.layout}>
          {/* FORM */}
          <section style={ui.card}>
            <div style={ui.cardPad}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <div>
                  <div style={{ fontWeight: 900, color: "#0f2f46" }}>
                    {editingId ? "Editar media" : "Nuevo media"}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(44,59,81,.62)" }}>
                    Sube el archivo o registra una URL reutilizable.
                  </div>
                </div>

                <button
                  type="button"
                  style={ui.miniBtn}
                  onClick={() => setShowForm((v) => !v)}
                >
                  {showForm ? "Ocultar" : "+ Nuevo"}
                </button>
              </div>

              {error && <div style={ui.error}>{error}</div>}

              {showForm && (
                <form onSubmit={onSubmit} style={{ display: "grid", gap: 12 }}>
                  <div>
                    <label style={ui.label}>Título</label>
                    <input
                      style={ui.input}
                      value={form.title}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, title: e.target.value }))
                      }
                      placeholder="Ej: ZN-0001-portada-arquitectura-del-enfoque"
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <div>
                      <label style={ui.label}>Tipo</label>
                      <select
                        style={ui.input}
                        value={form.kind}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, kind: e.target.value }))
                        }
                      >
                        <option value="image">Imagen</option>
                        <option value="video">Video</option>
                        <option value="audio">Audio</option>
                        <option value="embed">Embed</option>
                        <option value="file">Archivo</option>
                      </select>
                    </div>

                    <div>
                      <label style={ui.label}>Proveedor</label>
                      <input
                        style={ui.input}
                        value={form.provider}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, provider: e.target.value }))
                        }
                        placeholder="R2, Vimeo, YouTube..."
                      />
                    </div>
                  </div>

                  <div>
                    <label style={ui.label}>URL</label>
                    <input
                      style={ui.input}
                      value={form.url}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, url: e.target.value }))
                      }
                    />

                    <input
                      type="file"
                      accept="image/*,audio/*,video/*,.pdf"
                      onChange={handleFileUpload}
                      style={{ marginTop: 10 }}
                    />

                    {form.url && (
                      <div style={{ marginTop: 12 }}>
                        {form.kind === "image" && (
                          <div
                            style={{
                              width: "100%",
                              height: 180,
                              borderRadius: 16,
                              overflow: "hidden",
                              border: "1px solid rgba(44,59,81,.12)",
                              background: "#f8fafc",
                            }}
                          >
                            <img
                              src={form.url}
                              alt={form.title || "Vista previa"}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          </div>
                        )}

                        {form.kind === "audio" && (
                          <audio controls style={{ width: "100%" }}>
                            <source src={form.url} />
                          </audio>
                        )}

                        {form.kind === "video" && (
                          <div
                            style={{
                              width: "100%",
                              maxWidth: 600,
                              aspectRatio: isVertical ? "9 / 16" : "16 / 9",
                              marginTop: 12,
                            }}
                          >
                            <video
                              controls
                              onLoadedMetadata={(e) => {
                                const video = e.target;
                                setIsVertical(
                                  video.videoHeight > video.videoWidth
                                );
                              }}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                borderRadius: 8,
                              }}
                            >
                              <source src={form.url} />
                            </video>
                          </div>
                        )}

                        {form.kind === "file" && (
                          <a href={form.url} target="_blank" rel="noreferrer">
                            Ver archivo
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={ui.label}>Descripción</label>
                    <textarea
                      style={ui.textarea}
                      value={form.description}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, description: e.target.value }))
                      }
                      placeholder="Uso recomendado, contexto o nota interna."
                    />
                  </div>

                  <div>
                    <label style={ui.label}>Estado</label>
                    <select
                      style={ui.input}
                      value={form.status}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, status: e.target.value }))
                      }
                    >
                      <option value="draft">Borrador</option>
                      <option value="published">Publicado</option>
                      <option value="archived">Archivado</option>
                    </select>
                  </div>

                  <div style={ui.btnRow}>
                    {editingId && (
                      <button type="button" onClick={reset} style={ui.btn}>
                        Cancelar
                      </button>
                    )}

                    <button
                      type="submit"
                      style={ui.btnPrimary}
                      disabled={busy || uploading}
                    >
                      {uploading
                        ? "Subiendo archivo..."
                        : editingId
                        ? "Guardar"
                        : "Crear media"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>

          {/* LIBRARY */}
          <section style={ui.card}>
            <div style={ui.cardPad}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "flex-start",
                  marginBottom: 14,
                }}
              >
                <div>
                  <div style={{ fontWeight: 900, color: "#0f2f46" }}>
                    Biblioteca
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(44,59,81,.62)" }}>
                    {filteredItems.length} de {items.length} activos visibles
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr .7fr .8fr",
                  gap: 10,
                  marginBottom: 16,
                }}
              >
                <input
                  style={ui.input}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar por nombre, descripción o URL..."
                />

                <select
                  style={ui.input}
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value)}
                >
                  <option value="all">Todos los tipos</option>
                  <option value="image">Imágenes</option>
                  <option value="video">Videos</option>
                  <option value="audio">Audios</option>
                  <option value="file">Archivos</option>
                  <option value="embed">Embeds</option>
                </select>

                <select
                  style={ui.input}
                  value={useFilter}
                  onChange={(e) => setUseFilter(e.target.value)}
                >
                  <option value="all">Todos los usos</option>
                  <option value="Sistemas">Sistemas</option>
                  <option value="ZEDRO News">ZEDRO News</option>
                  <option value="Store">Store</option>
                  <option value="Otros">Otros</option>
                </select>
              </div>

              {loading ? (
                <p>Cargando...</p>
              ) : filteredItems.length === 0 ? (
                <div
                  style={{
                    padding: 22,
                    borderRadius: 16,
                    background: "#f8fafc",
                    border: "1px dashed rgba(44,59,81,.20)",
                    color: "rgba(44,59,81,.64)",
                    textAlign: "center",
                  }}
                >
                  No hay media con esos filtros.
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 14,
                      padding: "10px 12px",
                      borderRadius: 16,
                      background: "rgba(15,47,70,.035)",
                      border: "1px solid rgba(44,59,81,.08)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: "rgba(44,59,81,.66)",
                        fontWeight: 800,
                      }}
                    >
                      Mostrando {rangeStart}–{rangeEnd} de {filteredItems.length}
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        type="button"
                        onClick={goPreviousPage}
                        disabled={safeCurrentPage === 1}
                        style={{
                          ...ui.miniBtn,
                          opacity: safeCurrentPage === 1 ? 0.45 : 1,
                          cursor: safeCurrentPage === 1 ? "not-allowed" : "pointer",
                        }}
                      >
                        ← Anterior
                      </button>

                      <span
                        style={{
                          fontSize: 12,
                          color: "rgba(44,59,81,.62)",
                          fontWeight: 900,
                        }}
                      >
                        Página {safeCurrentPage} de {totalPages}
                      </span>

                      <button
                        type="button"
                        onClick={goNextPage}
                        disabled={safeCurrentPage === totalPages}
                        style={{
                          ...ui.miniBtn,
                          opacity: safeCurrentPage === totalPages ? 0.45 : 1,
                          cursor:
                            safeCurrentPage === totalPages ? "not-allowed" : "pointer",
                        }}
                      >
                        Siguiente →
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                      gap: 14,
                    }}
                  >
                  {visibleItems.map((it) => {
                    const useLabel = getUseLabel(it.title);

                    return (
                      <article
                        key={it.id}
                        style={{
                          border: "1px solid rgba(44,59,81,.12)",
                          borderRadius: 18,
                          overflow: "hidden",
                          background: "#fff",
                          boxShadow: "0 10px 24px rgba(15,47,70,.05)",
                        }}
                      >
                        <div
                          style={{
                            height: 132,
                            background: "#f8fafc",
                            borderBottom: "1px solid rgba(44,59,81,.08)",
                            display: "grid",
                            placeItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          {isImage(it) ? (
                            <img
                              src={it.url}
                              alt={it.title}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                fontSize: 12,
                                color: "rgba(44,59,81,.58)",
                                fontWeight: 800,
                                textTransform: "uppercase",
                              }}
                            >
                              {it.kind || "media"}
                            </div>
                          )}
                        </div>

                        <div style={{ padding: 12 }}>
                          <div
                            style={{
                              fontWeight: 900,
                              color: "#0f2f46",
                              lineHeight: 1.25,
                              marginBottom: 7,
                              minHeight: 38,
                            }}
                          >
                            {it.title}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              flexWrap: "wrap",
                              marginBottom: 10,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: "rgba(40,107,148,.08)",
                                color: "#264763",
                                fontWeight: 900,
                              }}
                            >
                              {it.kind}
                            </span>

                            <span
                              style={{
                                fontSize: 11,
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: "rgba(204,174,73,.12)",
                                color: "#8a6724",
                                fontWeight: 900,
                              }}
                            >
                              {useLabel}
                            </span>

                            <span
                              style={{
                                fontSize: 11,
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: "rgba(44,59,81,.06)",
                                color: "rgba(44,59,81,.64)",
                                fontWeight: 900,
                              }}
                            >
                              {it.status}
                            </span>
                          </div>

                          {it.url && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "rgba(44,59,81,.52)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                marginBottom: 10,
                              }}
                              title={it.url}
                            >
                              {it.url}
                            </div>
                          )}

                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            {it.url && (
                              <button
                                type="button"
                                onClick={() => copyUrl(it.url)}
                                style={ui.miniBtn}
                              >
                                Copiar
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => startEdit(it)}
                              style={ui.miniBtn}
                            >
                              Editar
                            </button>

                            <button
                              type="button"
                              onClick={() => remove(it.id)}
                              style={{ ...ui.miniBtn, ...ui.danger }}
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
                </>
              )}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
